"use client";

import { ApiErrorState } from "@/components/error/api-error-state";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAppSelector } from "@/redux/hooks";
import { ArrowUp, LayoutGrid, List } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso, VirtuosoGrid } from "react-virtuoso";

import { useSearchHolidayQuery } from "../../api/holiday-search-api";
import { filterHolidayResult } from "../../lib/filter-holiday-result";
import { toHolidaySearchQueryArgs } from "../../lib/holiday-formatter-helper";
import { useHolidayFilter } from "../../state/extract-holiday-filter-slice";
import { IHolidaySearchQueryParams } from "../../types/holiday-search-type";
import { HolidayFilterSidebar } from "../filters/holiday-filter-sidebar";
import { HolidayCardLoadingSkeleton } from "../loader/holiday-card-loading-skeleton";
import HolidayGridCard from "./holiday-grid-card";
import HolidayListCard from "./holiday-list-card";

type HolidaySearchProps = {
  decoded: IHolidaySearchQueryParams;
};

export default function HolidaySearch({ decoded }: HolidaySearchProps) {
  const { selectedOption } = useAppSelector(useHolidayFilter);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const searchQueryArgs = useMemo(
    () => toHolidaySearchQueryArgs(decoded),
    [decoded],
  );

  const { currentData, error, isLoading, isFetching, isError, refetch } =
    useSearchHolidayQuery(searchQueryArgs);

  const holiday_list = currentData?.data;
  const result = useMemo(
    () => filterHolidayResult(holiday_list, selectedOption),
    [holiday_list, selectedOption],
  );

  const handleRangeChange = useCallback((range: { startIndex: number }) => {
    const shouldShow = range.startIndex > 4;
    setShowBackToTop((prev) => (prev === shouldShow ? prev : shouldShow));
  }, []);

  const resultsRootRef = useRef<HTMLDivElement | null>(null);

  const renderListItem = useCallback(
    (index: number) => {
      const holiday = result[index];
      if (!holiday) return null;

      return (
        <div className="pt-4 md:pr-2">
          <HolidayListCard holiday={holiday} searchData={decoded} />
        </div>
      );
    },
    [decoded, result],
  );

  const renderGridItem = useCallback(
    (index: number) => {
      const holiday = result[index];
      if (!holiday) return null;

      return (
        <div className="h-full">
          <HolidayGridCard holiday={holiday} searchData={decoded} />
        </div>
      );
    },
    [decoded, result],
  );

  const virtuosoComponents = useMemo(
    () => ({
      Footer: () =>
        result.length > 0 ? (
          <div className="flex justify-center py-6 text-sm text-muted-foreground w-full col-span-full">
            No more packages
          </div>
        ) : null,
    }),
    [result.length],
  );

  // Scroll to top of results when filters change or when data finishes fetching
  useEffect(() => {
    if ((!isLoading || !isFetching) && !isError && result.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isLoading, isFetching, isError, result.length]);

  if (isError)
    return (
      <ApiErrorState
        error={error}
        title="Error fetching holiday packages"
        onRetry={() => void refetch()}
        className="container min-h-[50vh] py-12"
      />
    );

  return (
    <section className="container-wrapper">
      <div className="container px-0 relative flex w-full flex-1 flex-col lg:flex-row lg:items-start">
        <HolidayFilterSidebar isLoading={isLoading || isFetching} />

        <div className="overflow-hidden flex-1">
          <div className="md:mt-4">
            <div className="relative lg:ml-4">
              {/* Header Toolbar: Results count + Grid/List View Switcher */}
              <div className="flex items-center justify-between gap-2 pb-2 px-1">
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {isLoading || isFetching ? (
                      "Searching packages..."
                    ) : (
                      <>
                        <span>{result.length}</span>{" "}
                        <span className="text-muted-foreground font-normal">
                          {result.length === 1
                            ? "package found"
                            : "packages found"}
                        </span>
                      </>
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-1 rounded-lg border border-border/70 bg-muted/30 p-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewMode("grid")}
                    className={cn(
                      "h-7 w-7 p-0 rounded-md transition-all",
                      viewMode === "grid"
                        ? "bg-background text-primary shadow-xs font-semibold"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    aria-label="Grid view"
                    title="Grid view"
                  >
                    <LayoutGrid className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewMode("list")}
                    className={cn(
                      "h-7 w-7 p-0 rounded-md transition-all",
                      viewMode === "list"
                        ? "bg-background text-primary shadow-xs font-semibold"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    aria-label="List view"
                    title="List view"
                  >
                    <List className="size-3.5" />
                  </Button>
                </div>
              </div>

              {isLoading || isFetching ? (
                viewMode === "grid" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pt-3 md:pr-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <HolidayCardLoadingSkeleton key={i} viewMode="grid" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4 pt-3 pr-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <HolidayCardLoadingSkeleton key={i} viewMode="list" />
                    ))}
                  </div>
                )
              ) : result.length === 0 ? (
                <div className="rounded-xl border bg-background p-8 text-center mt-3">
                  <h3 className="text-base font-semibold">No packages found</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Try adjusting filters or search with another destination.
                  </p>
                </div>
              ) : (
                <div className="" ref={resultsRootRef}>
                  {viewMode === "grid" ? (
                    <VirtuosoGrid
                      useWindowScroll
                      data={result}
                      overscan={200}
                      rangeChanged={handleRangeChange}
                      listClassName="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pt-3 md:pr-2"
                      itemClassName="h-full"
                      itemContent={renderGridItem}
                      components={virtuosoComponents}
                    />
                  ) : (
                    <Virtuoso
                      useWindowScroll
                      data={result}
                      overscan={200}
                      rangeChanged={handleRangeChange}
                      itemContent={renderListItem}
                      components={virtuosoComponents}
                    />
                  )}
                </div>
              )}

              {showBackToTop && (
                <button
                  type="button"
                  aria-label="Back to top"
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                  className="fixed bottom-6 right-6 z-40 rounded-full bg-primary text-primary-foreground shadow-lg p-2 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <ArrowUp className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

